import { reactive, computed } from 'vue'
import axios from 'axios'

// Annonce de mise à jour diffusée par un administrateur à tous les utilisateurs.
// Pas de websockets sur l'hébergement : chaque onglet visible interroge une route
// publique très légère (un fichier lu côté serveur, pas de SQL, pas de throttle).
const ENDPOINT = '/api/system/announcement'
const POLL_INTERVAL_MS = 10000

const WARNING_SECONDS = 5 * 60
const CRITICAL_SECONDS = 60

const state = reactive({
  announcement: null,
  clockOffset: 0, // horloge serveur - horloge locale, pour un compte à rebours identique partout
  now: Date.now()
})

let started = false
let inflight = false
let tickTimer = null
let reloading = false

function applyPayload(data) {
  if (typeof data?.server_time === 'number') {
    state.clockOffset = data.server_time - Date.now()
  }
  state.announcement = data?.announcement || null
  state.now = Date.now()
  syncTicker()
}

function syncTicker() {
  if (state.announcement && !tickTimer) {
    tickTimer = setInterval(() => { state.now = Date.now() }, 1000)
  } else if (!state.announcement && tickTimer) {
    clearInterval(tickTimer)
    tickTimer = null
  }
}

async function refresh() {
  if (inflight || reloading) return
  inflight = true
  try {
    // fetch plutôt qu'axios : pas d'en-tête d'authentification inutile, et on échappe
    // à l'intercepteur global qui redirige les 503 vers /error/500
    const res = await fetch(ENDPOINT, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin'
    })

    if (res.status === 503) {
      // `php artisan down` vient d'être lancé : on recharge pour afficher la page de
      // maintenance, qui rechargera elle-même la page courante au retour du site
      reloading = true
      window.location.reload()
      return
    }

    if (res.ok) {
      applyPayload(await res.json())
    }
  } catch {
    // Réseau momentanément indisponible : nouvel essai au prochain tour
  } finally {
    inflight = false
  }
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') refresh()
}

function startPolling() {
  if (started) return
  started = true
  refresh()
  // Les onglets en arrière-plan ne sollicitent pas le serveur : ils se mettent à jour
  // dès qu'on revient dessus
  setInterval(() => {
    if (document.visibilityState === 'visible') refresh()
  }, POLL_INTERVAL_MS)
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('online', refresh)
}

export function formatCountdown(totalSeconds) {
  const s = Math.max(0, Math.ceil(totalSeconds))
  const hours = Math.floor(s / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  const seconds = s % 60
  if (hours > 0) {
    return `${hours} h ${String(minutes).padStart(2, '0')} min`
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export function useMaintenanceNotice() {
  const announcement = computed(() => state.announcement)

  const secondsLeft = computed(() => {
    if (!state.announcement) return null
    const scheduledAt = Date.parse(state.announcement.scheduled_at)
    return (scheduledAt - (state.now + state.clockOffset)) / 1000
  })

  const totalSeconds = computed(() => {
    if (!state.announcement) return null
    const { created_at: createdAt, scheduled_at: scheduledAt } = state.announcement
    return Math.max(1, (Date.parse(scheduledAt) - Date.parse(createdAt)) / 1000)
  })

  // info → warning (≤ 5 min) → critical (≤ 1 min) → overdue (échéance passée)
  const phase = computed(() => {
    const left = secondsLeft.value
    if (left === null) return null
    if (left <= 0) return 'overdue'
    if (left <= CRITICAL_SECONDS) return 'critical'
    if (left <= WARNING_SECONDS) return 'warning'
    return 'info'
  })

  // Fraction de temps restante (1 → 0), pour l'anneau de progression
  const remainingRatio = computed(() => {
    if (secondsLeft.value === null) return 0
    return Math.min(1, Math.max(0, secondsLeft.value / totalSeconds.value))
  })

  const countdownLabel = computed(() =>
    secondsLeft.value === null ? '' : formatCountdown(secondsLeft.value)
  )

  const announce = async (minutes, message) => {
    const { data } = await axios.post('/api/admin/system/announcement', { minutes, message })
    applyPayload(data)
    return data
  }

  const cancel = async () => {
    const { data } = await axios.delete('/api/admin/system/announcement')
    applyPayload({ announcement: null })
    return data
  }

  return {
    announcement,
    secondsLeft,
    phase,
    remainingRatio,
    countdownLabel,
    startPolling,
    refresh,
    announce,
    cancel
  }
}
